import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Building2, User, Mail, Phone, Building, Users, Briefcase } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser, isRegistering } = useAuth();
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const password = watch('password');
  const selectedRole = watch('role', '');

  const onSubmit = (data) => {
    const payload =
      data.role === 'rental_management_company'
        ? {
            ...data,
            company_email: data.email,
          }
        : data;

    registerUser(payload);
  };

  const roles = [
    {
      value: 'rental_management_company',
      label: '임대관리사업자',
      description: '임대사업자들의 임대업무를 대행하는 사업자',
      icon: Building,
      color: 'bg-blue-500'
    },
    {
      value: 'property_owner',
      label: '임대사업자',
      description: '실제 건물을 소유하고 임대하는 사업자',
      icon: Building2,
      color: 'bg-green-500'
    },
    {
      value: 'tenant_manager',
      label: '임대관리자',
      description: '임대관리사업자의 직원',
      icon: Users,
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        {/* 로고 및 제목 */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            RentalHub
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            임대관리 시스템에 가입하세요
          </p>
        </div>

        {/* 회원가입 폼 */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* 역할 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              가입 유형을 선택하세요
            </label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {roles.map((role) => {
                const Icon = role.icon;
                return (
                  <div
                    key={role.value}
                    className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedRole === role.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() =>
                      setValue('role', role.value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      })
                    }
                  >
                    <input
                      type="radio"
                      value={role.value}
                      {...register('role', { required: '가입 유형을 선택해주세요' })}
                      checked={selectedRole === role.value}
                      onChange={() =>
                        setValue('role', role.value, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        })
                      }
                      className="sr-only"
                    />
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full ${role.color} flex items-center justify-center mr-3`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{role.label}</h3>
                        <p className="text-xs text-gray-500">{role.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          <div className="space-y-4">
            {/* 이름 입력 */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                이름
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className={`pl-10 appearance-none relative block w-full px-3 py-2 border ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="이름을 입력하세요"
                  {...register('name', {
                    required: '이름을 입력해주세요',
                    minLength: {
                      value: 2,
                      message: '이름은 최소 2자 이상이어야 합니다',
                    },
                  })}
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* 이메일 입력 */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                이메일
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`pl-10 appearance-none relative block w-full px-3 py-2 border ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="이메일을 입력하세요"
                  {...register('email', {
                    required: '이메일을 입력해주세요',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: '유효한 이메일 주소를 입력해주세요',
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* 전화번호 입력 */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                전화번호
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  className={`pl-10 appearance-none relative block w-full px-3 py-2 border ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="전화번호를 입력하세요 (예: 010-1234-5678)"
                  {...register('phone', {
                    required: '전화번호를 입력해주세요',
                    pattern: {
                      value: /^[0-9-]+$/,
                      message: '숫자와 하이픈(-)만 입력 가능합니다',
                    },
                  })}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>

            {/* 임대관리사업자 추가 정보 */}
            {selectedRole === 'rental_management_company' && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900">임대관리사업자 정보</h3>
                
                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                    회사명
                  </label>
                  <input
                    id="company_name"
                    name="company_name"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="회사명을 입력하세요"
                    {...register('company_name', { required: '회사명을 입력해주세요' })}
                  />
                </div>

                <div>
                  <label htmlFor="business_number" className="block text-sm font-medium text-gray-700">
                    사업자등록번호
                  </label>
                  <input
                    id="business_number"
                    name="business_number"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="123-45-67890"
                    {...register('business_number', { required: '사업자등록번호를 입력해주세요' })}
                  />
                </div>

                <div>
                  <label htmlFor="company_address" className="block text-sm font-medium text-gray-700">
                    회사 주소
                  </label>
                  <input
                    id="company_address"
                    name="company_address"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="회사 주소를 입력하세요"
                    {...register('company_address', { required: '회사 주소를 입력해주세요' })}
                  />
                </div>

                <div>
                  <label htmlFor="company_phone" className="block text-sm font-medium text-gray-700">
                    회사 전화번호
                  </label>
                  <input
                    id="company_phone"
                    name="company_phone"
                    type="tel"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="02-1234-5678"
                    {...register('company_phone', { required: '회사 전화번호를 입력해주세요' })}
                  />
                </div>

                <div>
                  <label htmlFor="company_email" className="block text-sm font-medium text-gray-700">
                    회사 이메일
                  </label>
                  <input
                    id="company_email"
                    name="company_email"
                    type="email"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="company@example.com"
                    {...register('company_email', { required: '회사 이메일을 입력해주세요' })}
                  />
                </div>

                <div>
                  <label htmlFor="representative_name" className="block text-sm font-medium text-gray-700">
                    대표자명
                  </label>
                  <input
                    id="representative_name"
                    name="representative_name"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="대표자명을 입력하세요"
                    {...register('representative_name', { required: '대표자명을 입력해주세요' })}
                  />
                </div>
              </div>
            )}

            {/* 임대사업자 추가 정보 */}
            {selectedRole === 'property_owner' && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-medium text-gray-900">임대사업자 정보</h3>
                
                <div>
                  <label htmlFor="rental_management_company_id" className="block text-sm font-medium text-gray-700">
                    임대관리사업자 ID
                  </label>
                  <input
                    id="rental_management_company_id"
                    name="rental_management_company_id"
                    type="number"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="임대관리사업자 ID를 입력하세요"
                    {...register('rental_management_company_id', { required: '임대관리사업자 ID를 입력해주세요' })}
                  />
                </div>

                <div>
                  <label htmlFor="owner_address" className="block text-sm font-medium text-gray-700">
                    주소
                  </label>
                  <input
                    id="owner_address"
                    name="owner_address"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="주소를 입력하세요"
                    {...register('owner_address')}
                  />
                </div>

                <div>
                  <label htmlFor="bank_name" className="block text-sm font-medium text-gray-700">
                    은행명
                  </label>
                  <input
                    id="bank_name"
                    name="bank_name"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="은행명을 입력하세요"
                    {...register('bank_name')}
                  />
                </div>

                <div>
                  <label htmlFor="bank_account" className="block text-sm font-medium text-gray-700">
                    계좌번호
                  </label>
                  <input
                    id="bank_account"
                    name="bank_account"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="계좌번호를 입력하세요"
                    {...register('bank_account')}
                  />
                </div>

                <div>
                  <label htmlFor="account_holder" className="block text-sm font-medium text-gray-700">
                    예금주
                  </label>
                  <input
                    id="account_holder"
                    name="account_holder"
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="예금주명을 입력하세요"
                    {...register('account_holder')}
                  />
                </div>
              </div>
            )}

            {/* 비밀번호 입력 */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                비밀번호
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="비밀번호를 입력하세요"
                  {...register('password', {
                    required: '비밀번호를 입력해주세요',
                    minLength: {
                      value: 6,
                      message: '비밀번호는 최소 6자 이상이어야 합니다',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* 비밀번호 확인 입력 */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                비밀번호 확인
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                  placeholder="비밀번호를 다시 입력하세요"
                  {...register('confirmPassword', {
                    required: '비밀번호 확인을 입력해주세요',
                    validate: (value) =>
                      value === password || '비밀번호가 일치하지 않습니다',
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* 회원가입 버튼 */}
          <div>
            <button
              type="submit"
              disabled={isRegistering}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRegistering ? (
                <div className="loading-spinner mr-2"></div>
              ) : null}
              {isRegistering ? '가입 중...' : '회원가입'}
            </button>
          </div>

          {/* 로그인 링크 */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              이미 계정이 있으신가요?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                로그인
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
